import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { 
  TrendingUp, 
  ArrowLeft, 
  Send, 
  MessageSquare,
  Briefcase,
  FileText,
  UserPlus,
  RefreshCw,
  Trophy,
  MoreHorizontal
} from 'lucide-react';
import api from '../lib/axios';
import { toast } from 'sonner';

function CareerUpdate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    update_type: '',
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const updateTypes = [
    { value: 'Oferta de Club', icon: Briefcase, color: 'bg-blue-500/20 text-blue-400' },
    { value: 'Renovación Contrato', icon: FileText, color: 'bg-green-500/20 text-green-400' },
    { value: 'Interés de Agente', icon: UserPlus, color: 'bg-purple-500/20 text-purple-400' },
    { value: 'Cambio de Situación', icon: RefreshCw, color: 'bg-orange-500/20 text-orange-400' },
    { value: 'Logro Deportivo', icon: Trophy, color: 'bg-yellow-500/20 text-yellow-400' },
    { value: 'Otro', icon: MoreHorizontal, color: 'bg-zinc-500/20 text-zinc-400' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.update_type || !formData.description) {
      toast.error('Completa todos los campos');
      return;
    }

    setSubmitting(true);
    
    try {
      await api.post('/career-updates', formData);
      toast.success('Actualización enviada. Tu agencia ha sido notificada.');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error submitting career update:', error);
      toast.error('Error al enviar actualización');
    } finally {
      setSubmitting(false);
    }
  };

  if (user?.role !== 'player') {
    return (
      <Layout>
        <div className="px-4 py-8 max-w-lg mx-auto">
          <div className="card-dark p-8 text-center">
            <TrendingUp className="w-12 h-12 text-zinc-400 mx-auto mb-4" />
            <p className="text-zinc-400">Solo disponible para jugadores</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-[#050505]" data-testid="career-update-page">
        {/* Header */}
        <div className="sticky top-14 lg:top-0 z-30 bg-[#050505]/95 backdrop-blur-xl border-b border-white/10 px-4 py-3">
          <div className="max-w-lg mx-auto flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="p-2 -ml-2 text-zinc-400 active:scale-95">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Oswald' }}>ACTUALIZAR CARRERA</h1>
                <p className="text-xs text-zinc-500">Novedades importantes</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 py-6 max-w-lg mx-auto pb-32 space-y-6">
          
          {/* Update Type Selection */}
          <div>
            <label className="text-sm text-zinc-400 mb-3 block">Tipo de novedad</label>
            <div className="grid grid-cols-2 gap-3">
              {updateTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, update_type: type.value })}
                    className={`p-4 rounded-lg border text-left transition-all active:scale-95 flex items-center gap-3 ${
                      formData.update_type === type.value 
                        ? type.color + ' border-white/30' 
                        : 'bg-zinc-900 border-zinc-800 text-zinc-400'
                    }`}
                    data-testid={`update-type-${type.value.toLowerCase().replace(/ /g, '-')}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      formData.update_type === type.value ? type.color : 'bg-zinc-800'
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className="text-sm font-medium">{type.value}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-sm text-zinc-400 mb-2 block">Descripción</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe la novedad en detalle..."
              rows={6}
              className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-400 text-base resize-none"
              data-testid="career-update-description-textarea"
            />
          </div>

          {/* WhatsApp Notice */}
          <div className="card-dark p-4 flex items-start gap-3 border-l-4 border-green-500">
            <MessageSquare className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-zinc-300">
              Tu agencia recibirá una <strong className="text-green-400">notificación WhatsApp</strong> con esta actualización.
            </p>
          </div>
        </div>

        {/* Fixed Bottom Action */}
        <div className="fixed bottom-16 lg:bottom-0 left-0 right-0 p-4 bg-[#050505]/95 backdrop-blur-xl border-t border-white/10 safe-area-bottom">
          <div className="max-w-lg mx-auto flex gap-3">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="h-14 px-6 border-zinc-700 text-white hover:bg-zinc-800"
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !formData.update_type}
              className="flex-1 h-14 bg-green-500 hover:bg-green-600 text-white font-bold text-base uppercase tracking-wide active:scale-95 transition-transform"
              data-testid="submit-career-update-button"
            >
              {submitting ? 'Enviando...' : 'Enviar'}
              <Send className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default CareerUpdate;
